<template>
  <fieldset class="contact-phone"
            :class="{ 'app-embedded-fieldset': embedded }"><!-- using fieldset as verification code was expected in future release -->
    <legend v-if="titlePhone=='Phone'">
      <span>{{ titlePhone }}<span>(xxx-xxx-xxxx)</span><span>(Message and data rates may apply.)</span></span>
    </legend>
    <legend v-else>
      <span>{{ titlePhone }}</span>
    </legend>
    <span class="app-contact-inputs">
      <ValidationObserver ref="phoneNumObserver" mode="lazy">
        <div class="app-input-group app-contact-info" :class="{ 'app-invalid-group': invalidPhone || invalidPhoneNumChars }">
          <div class="app-phone-number">
            <ValidationProvider rules="numeric|length:3">
              <label for="userPhoneAC" class="sr-only">Area code</label>
              <input id="userPhoneAC" ref="userPhoneAC" type="tel" v-model="myPhoneAC"
                     @keyup="switchFocus" :readonly="editable" :disabled="editable" oncopy="return false" onpaste="return false">
            </ValidationProvider> -
            <ValidationProvider rules="numeric|length:3">
              <label for="userPhoneF3" class="sr-only">First three digits</label>
              <input id="userPhoneF3" ref="userPhoneF3" type="tel" v-model="myPhoneF3"
                     @keyup="switchFocus" :readonly="editable" :disabled="editable" oncopy="return false" onpaste="return false">
            </ValidationProvider> -
            <ValidationProvider rules="numeric|length:4">
              <label for="userPhoneL4" class="sr-only">Last four digits</label>
              <input id="userPhoneL4" ref="userPhoneL4" type="tel" v-model="myPhoneL4"
                     @keyup="switchFocus" :readonly="editable" :disabled="editable" oncopy="return false" onpaste="return false">
              <p v-if="invalidPhoneNumChars">Phone number contains invalid characters.</p>
              <p v-else-if="invalidPhone">Please enter a valid phone number.</p>
            </ValidationProvider>
          </div>
        </div>
      </ValidationObserver>
    </span>
  </fieldset>
</template>

<script>
import { mapState } from 'vuex'
import { phoneMixin } from '@/utils/phoneMixin'
import { setInteractionMode , ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { length, numeric } from 'vee-validate/dist/rules'

extend('length', {
  ...length,
  message: (field, params) => {
    return `${params._field_} must have 10 digits.`;
  }
});
extend('numeric', numeric);

export default {
  name: 'ContactPhone',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    embedded: {             // embedded fieldset (in Step 5)?
      type: Boolean,
      required: false,
      default: false,
    },
    isModal: {              // SPECIAL: see comment in myPhone watcher
      type: Boolean,
      required: false,
      default: false,
    },
    verify: {
      type: Boolean,
      required: false,
      default: false,
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
    titlePhone: {
      type: String,
      required: false,
      default: 'Phone',
    }
  },
  mixins: [
    phoneMixin,
  ],
  computed: {
    ...mapState([
      // 'reservationSlotId',
      // 'resDetails',
    ]),
    invalidPhoneNumChars() {
      const phoneNum = this.myPhoneAC + this.myPhoneF3 + this.myPhoneL4;
      if (phoneNum.length === 0) {
        return false;
      }
      const pattern = /^\d+$/;
      return !pattern.test(phoneNum);
    },
  },
  data() {
    return {
      myPhoneAC: '',
      myPhoneF3: '',
      myPhoneL4: '',
      myPhone: '',
      invalidPhone: false,
      isShiftTab: false,
    };
  },
  watch: {
    myPhoneAC() {
      this.updateMyPhone('myPhoneAC');
    },
    myPhoneF3() {
      this.updateMyPhone('myPhoneF3');
    },
    myPhoneL4() {
      this.updateMyPhone('myPhoneL4');
    },
    /**
     * SPECIAL handling of modal phone check.
     * !!! verify prop value is NOT consistently being passed to child components from within a modal !!!
     * !!! Vue $emit (to component parent) is not aware of child's await Promise call so they are cross-wired !!!
     * Must do extra checks since we cannot depend on parent ModalContact's bound variable to tell us when user is done.
     */
    myPhone() {
      // for both Step 5 (after updated to type=tel) and Step 6 modal
      // len checks to prevent instant validation as soon as user has keyed
      // myPhone will be '' if there are invalid chars
      if (this.myPhone.length === 0 || this.myPhone.length === 10) {
        this.checkPhone();
      }
    },
    /**
     * For Step 5 (non-modal) only. Step 6 modal depends on myPhone watcher.
     * Modal props do not behave like regular components (Step 5) where a child does not always detect
     * its parent's bound variable when it's changed.
     */
    async verify() {
      // MUST depend on parent to signal user's intention to move on in Step 5
      // !!! See comment in myPhone watcher !!!
      if (this.verify) {
        await this.checkPhone();
      }
    },
  },
  created() {
    setInteractionMode('aggressive');
  },
  mounted() {
    // pre-fill if info is available
    // console.log('CARTFINAL', this.$store.state.guestLogin.finalizationCart)
    if (this.$store.state.guestLogin.finalizationCart && this.$store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart) {
      const info = this.$store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart;
      if (info && info.phone && info.phone.length > 0) {
        const phoneNum = this.formatPhoneForWebapp(info.phone);
        if (phoneNum.length === 10) {
          this.myPhone = phoneNum;
          this.split(phoneNum);
        }
      }
    }
  },
  methods: {
    async checkPhone() {
      if(this.invalidPhoneNumChars) {
        return; 
      }
      this.invalidPhone = !await this.hasValidPhone();
       

      if (this.invalidPhone) {
        this.$emit('doResetVerify')
      }

      // pass info back to parent (UserContact or ModalContact)
      this.$emit('doPhone', { phone: this.myPhone, isValid: !this.invalidPhone });
    },
    /**
     * Applicable for both Step 5 and Step 6 modal.
     * @returns {Promise<boolean>}
     */
    async hasValidPhone() {
      if (this.invalidPhoneNumChars) {
        return false;
      }

      const phoneNumValid = await this.$refs.phoneNumObserver.validate();
      return (this.myPhone.length === 0 || (phoneNumValid && this.myPhone.length === 10));
    },
    split(num) {
      // split 10-digit phone number to 3 UI parts
      this.myPhoneAC = num.substr(0, 3);
      this.myPhoneF3 = num.substr(3, 3);
      this.myPhoneL4 = num.substr(6, 4);
    },
    async stitch() {
      if (this.invalidPhoneNumChars) {
        this.myPhone = '';
      } else {
        this.myPhone = `${this.myPhoneAC}${this.myPhoneF3}${this.myPhoneL4}`;
      }
    },
    switchFocus(e) {
      const isTab = e.key === 'Tab' || e.keyCode === 9;
      const isShift = e.key === 'Shift' || e.keyCode === 16;

      if (!isTab && !isShift) {
        const ref = e.target.id;
        switch (ref) {
          case 'userPhoneAC':
            if (this.myPhoneAC.length === 3) {
              this.$refs.userPhoneF3.focus();
            }
            break;
          case 'userPhoneF3':
            if (this.myPhoneF3.length === 3) {
              this.$refs.userPhoneL4.focus();
            }
            break;
          case 'userPhoneL4':
          default:
            // userPhoneL4 case no longer applicable in refactored component
        }
      } 
    },
    /**
     * Stop user from entering more digits than allowed. This is necessary when using input type=numbers where
     * there is no native HTML maxlength validation (maxlength is only available for type=text).
     * @param data
     * @returns {*|boolean|string}
     */
    truncateMax(data) {
      const partial = data.partial;
      const max = data.max;
      if (partial.length > max) {
        return partial.substr(0, max);
      }
      return partial;
    },
    updateMyPhone(field) {
      switch (field) {
        case 'myPhoneL4':   // go first since the other two fields will normally auto-advance based on length
          this.myPhoneL4 = this.truncateMax({ partial: this.myPhoneL4, max: 4, min: 4 });
          break;
        case 'myPhoneF3':
          this.myPhoneF3 = this.truncateMax({ partial: this.myPhoneF3, max: 3, min: 3 });
          break;
        case 'myPhoneAC':
          this.myPhoneAC = this.truncateMax({ partial: this.myPhoneAC, max: 3, min: 3 });
          break;
        default:
          // nada
      }

      // construct myPhone
      this.stitch();
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@ridmv/web-themes/styles/contact";
</style>
